/**
 * Forum Service - Core forum management and message orchestration
 *
 * This service manages multi-user discussion forums for collaborative trip planning,
 * handling thread creation, message posting, user permissions, and real-time updates.
 *
 * ARCHITECTURE UPDATE (Phase 1.1):
 * - Migrated from in-memory Maps to Firestore persistence
 * - Delegates CRUD operations to discussionFirestoreService
 * - Maintains permission checking and event publishing logic
 * - Supports effectiveUserId pattern for shared trips
 *
 * Key Responsibilities:
 * - Manage discussion forums for trips
 * - Handle thread creation and organization (metadata only, messages in Firestore)
 * - Process message posting and threading
 * - Manage user permissions and roles (in-memory cache)
 * - Coordinate real-time forum updates via events
 *
 * Event Integration:
 * - Subscribes to: MESSAGE_POSTED, THREAD_CREATED, USER_JOINED_TRIP, PERMISSION_CHANGED
 * - Publishes: FORUM_MESSAGE_RECEIVED, THREAD_UPDATED, USER_MENTIONED, FORUM_ACTIVITY
 */

import { eventBridgeService } from '@trip-planner-monorepo/shared';
import * as discussionFirestoreService from './discussionFirestoreService.js';

class ForumService {
  constructor() {
    this.isInitialized = false;

    // ARCHITECTURE CHANGE: Removed in-memory Maps for messages/threads
    // Messages now stored in Firestore via discussionFirestoreService

    // Retained in-memory caches for performance:
    this.activeForums = new Map(); // Forum metadata cache (participants, settings)
    this.userPermissions = new Map(); // User permissions cache
    this.notificationSettings = new Map(); // User notification preferences
  }

  /**
   * Initialize the forum service
   */
  init() {
    if (this.isInitialized) {
      console.warn('ForumService already initialized');
      return;
    }

    // Register event listeners
    eventBridgeService.subscribe('MESSAGE_POSTED', this.handleMessagePost.bind(this));
    eventBridgeService.subscribe('THREAD_CREATED', this.handleThreadCreation.bind(this));
    eventBridgeService.subscribe('USER_JOINED_TRIP', this.handleUserJoinTrip.bind(this));
    eventBridgeService.subscribe('PERMISSION_CHANGED', this.handlePermissionChange.bind(this));

    this.isInitialized = true;
    console.log('ForumService initialized');
  }

  /**
   * Create or get forum for a trip
   * @param {string} tripId - Trip identifier
   * @param {string} ownerId - Trip owner identifier
   * @returns {Object} Forum instance
   */
  async createTripForum(tripId, ownerId) {
    let forum = this.activeForums.get(tripId);

    if (!forum) {
      forum = {
        tripId,
        ownerId,
        createdAt: new Date().toISOString(),
        // ARCHITECTURE CHANGE: Removed threads Map - messages now in Firestore
        participants: new Map(),
        settings: {
          allowGuestPosting: false,
          moderationRequired: false,
          messagesPerPage: 50
        },
        statistics: {
          totalMessages: 0,
          activeParticipants: 0
        }
      };

      // Add owner as admin
      forum.participants.set(ownerId, {
        userId: ownerId,
        role: 'admin',
        joinedAt: new Date().toISOString(),
        permissions: ['create_threads', 'post_messages', 'invite_users', 'manage_permissions'],
        notificationSettings: {
          newThreads: true,
          newMessages: true,
          mentions: true
        }
      });

      this.activeForums.set(tripId, forum);

      eventBridgeService.publish('FORUM_CREATED', {
        tripId,
        ownerId,
        timestamp: new Date().toISOString()
      });
    }

    return forum;
  }

  /**
   * Create a new discussion thread
   *
   * PHASE 1 SIMPLIFICATION: Thread metadata only
   * - For Phase 1, we use a single 'main' thread for all trip discussions
   * - This method creates thread metadata but messages go directly to Firestore
   * - Future phases may add proper thread support with separate Firestore collection
   *
   * @param {string} tripId - Trip identifier
   * @param {string} userId - Creator identifier
   * @param {Object} threadData - Thread information
   * @returns {string} Thread ID
   */
  async createThread(tripId, userId, threadData) {
    const forum = this.activeForums.get(tripId);
    if (!forum) {
      throw new Error(`Forum not found for trip: ${tripId}`);
    }

    // Check permissions
    if (!this.hasPermission(userId, tripId, 'create_threads')) {
      throw new Error('User does not have permission to create threads');
    }

    // For Phase 1, just return 'main' thread ID
    // All messages go to the same discussion stream
    const threadId = threadData.threadId || 'main';

    // Publish thread creation event
    eventBridgeService.publish('THREAD_CREATED', {
      tripId,
      threadId,
      userId,
      title: threadData.title || 'Trip Discussion',
      category: threadData.category || 'general',
      timestamp: new Date().toISOString()
    });

    // Notify participants
    await this.notifyParticipants(tripId, 'new_thread', {
      threadId,
      title: threadData.title || 'Trip Discussion',
      createdBy: userId
    });

    console.log(`[forumService] Thread created: ${threadId} for trip ${tripId}`);
    return threadId;
  }

  /**
   * Post a message to a thread
   *
   * ARCHITECTURE CHANGE: Now uses Firestore instead of in-memory storage
   * - Delegates to discussionFirestoreService.postMessage()
   * - Maintains permission checking and event publishing
   * - effectiveUserId must be passed by caller (from TripContext)
   *
   * @param {string} tripId - Trip identifier
   * @param {string} threadId - Thread identifier (currently 'main' for all messages)
   * @param {string} userId - Message author (senderId)
   * @param {Object} messageData - Message content and metadata
   * @param {string} messageData.content - Message text
   * @param {string} messageData.senderName - Display name
   * @param {string} [messageData.effectiveUserId] - Trip owner ID for shared trips (REQUIRED)
   * @param {string} [messageData.senderAvatar] - Avatar URL
   * @param {string} [messageData.type='text'] - Message type
   * @param {Object} [messageData.metadata] - Additional metadata
   * @returns {string} Message ID
   */
  async postMessage(tripId, threadId, userId, messageData) {
    try {
      // Extract effectiveUserId from messageData (caller responsibility)
      const effectiveUserId = messageData.effectiveUserId || userId;
      if (!effectiveUserId) {
        throw new Error('effectiveUserId is required for posting messages');
      }

      // Permission check (still uses in-memory cache)
      const forum = this.activeForums.get(tripId);
      if (forum) {
        if (!this.hasPermission(userId, tripId, 'post_messages')) {
          throw new Error('User does not have permission to post messages');
        }
      }

      // Extract mentions from content
      const mentions = this.extractMentions(messageData.content || '');

      // Prepare message data for Firestore
      const firestoreMessageData = {
        senderId: userId,
        senderName: messageData.senderName,
        senderAvatar: messageData.senderAvatar,
        text: messageData.content,
        type: messageData.type || 'text',
        proposal: messageData.proposal || null,
        metadata: {
          threadId, // Store thread reference in metadata
          ...(messageData.metadata || {})
        }
      };

      // Delegate to Firestore service
      const messageId = await discussionFirestoreService.postMessage(
        effectiveUserId,
        tripId,
        firestoreMessageData
      );

      // Update forum statistics if forum is cached
      if (forum) {
        forum.statistics.totalMessages = (forum.statistics.totalMessages || 0) + 1;

        // Update thread statistics if threads are being tracked
        const thread = forum.threads?.get(threadId);
        if (thread) {
          thread.lastActivity = new Date().toISOString();
          thread.participants.add(userId);
          thread.statistics.messageCount++;
          thread.statistics.participantCount = thread.participants.size;
        }
      }

      // Publish message event for cross-service coordination
      eventBridgeService.publish('MESSAGE_POSTED', {
        tripId,
        threadId,
        messageId,
        authorId: userId,
        content: messageData.content,
        mentions,
        timestamp: new Date().toISOString()
      });

      // Handle mentions
      if (mentions.length > 0) {
        await this.processMentions({
          id: messageId,
          tripId,
          threadId,
          authorId: userId,
          mentions
        });
      }

      console.log(`[forumService] Message posted successfully: ${messageId}`);
      return messageId;

    } catch (error) {
      console.error('[forumService] Error posting message:', error);
      throw error;
    }
  }

  /**
   * Add user to trip forum
   * @param {string} tripId - Trip identifier
   * @param {string} userId - User to add
   * @param {string} role - User role (admin, collaborator, viewer)
   * @param {string} invitedBy - User who sent invitation
   */
  async addUserToForum(tripId, userId, role = 'collaborator', invitedBy) {
    const forum = this.activeForums.get(tripId);
    if (!forum) {
      throw new Error(`Forum not found for trip: ${tripId}`);
    }

    // Check if inviter has permission
    if (!this.hasPermission(invitedBy, tripId, 'invite_users')) {
      throw new Error('User does not have permission to invite others');
    }

    const permissions = this.getRolePermissions(role);

    const participant = {
      userId,
      role,
      joinedAt: new Date().toISOString(),
      invitedBy,
      permissions,
      notificationSettings: {
        newThreads: true,
        newMessages: role !== 'viewer',
        mentions: true
      }
    };

    forum.participants.set(userId, participant);
    forum.statistics.activeParticipants++;

    eventBridgeService.publish('USER_JOINED_FORUM', {
      tripId,
      userId,
      role,
      invitedBy,
      timestamp: new Date().toISOString()
    });

    return participant;
  }

  /**
   * Get forum threads with pagination
   *
   * PHASE 1 SIMPLIFICATION: Returns single 'main' thread
   * - For Phase 1, all discussions happen in one thread per trip
   * - Returns metadata for the main discussion thread
   * - Future phases may query Firestore for multiple threads
   *
   * @param {string} tripId - Trip identifier
   * @param {Object} options - Query options (mostly ignored in Phase 1)
   * @returns {Object} Thread list (single 'main' thread)
   */
  async getForumThreads(tripId, options = {}) {
    const forum = this.activeForums.get(tripId);

    // Phase 1: Return single 'main' thread
    const mainThread = {
      id: 'main',
      tripId,
      title: 'Trip Discussion',
      description: 'General discussion for this trip',
      category: 'general',
      createdBy: forum?.ownerId || 'system',
      createdAt: forum?.createdAt || new Date().toISOString(),
      lastActivity: new Date().toISOString(),
      isPinned: false,
      isLocked: false,
      tags: [],
      statistics: {
        messageCount: forum?.statistics?.totalMessages || 0,
        viewCount: 0,
        participantCount: forum?.statistics?.activeParticipants || 0
      }
    };

    return {
      threads: [mainThread],
      total: 1,
      hasMore: false,
      page: 1,
      limit: 20
    };
  }

  /**
   * Get thread messages with pagination
   *
   * ARCHITECTURE CHANGE: Now retrieves from Firestore
   * - Delegates to discussionFirestoreService.getMessages()
   * - Supports pagination via lastDoc cursor
   * - effectiveUserId must be passed via options
   *
   * @param {string} tripId - Trip identifier
   * @param {string} threadId - Thread identifier (currently not used for filtering)
   * @param {Object} options - Query options
   * @param {string} options.effectiveUserId - Trip owner ID for shared trips (REQUIRED)
   * @param {number} [options.limit=50] - Messages per page
   * @param {Object} [options.startAfter] - Last document for pagination
   * @param {string} [options.orderDirection='asc'] - Sort order
   * @returns {Object} Paginated message list
   */
  async getThreadMessages(tripId, threadId, options = {}) {
    try {
      const {
        effectiveUserId,
        limit = 50,
        startAfter = null,
        orderDirection = 'asc'
      } = options;

      if (!effectiveUserId) {
        throw new Error('effectiveUserId is required for retrieving messages');
      }

      // Delegate to Firestore service
      const result = await discussionFirestoreService.getMessages(effectiveUserId, tripId, {
        limit,
        startAfter,
        orderDirection,
        orderBy: 'timestamp'
      });

      // Filter deleted messages for non-admin users
      // TODO: Implement admin check when user roles are available
      const filteredMessages = result.messages.filter(msg => !msg.isDeleted);

      // Get thread metadata from cache if available
      const forum = this.activeForums.get(tripId);
      const thread = forum?.threads?.get(threadId);

      return {
        messages: filteredMessages,
        total: filteredMessages.length, // Note: This is page total, not collection total
        hasMore: result.hasMore,
        lastDoc: result.lastDoc,
        limit,
        thread: thread ? {
          id: thread.id,
          title: thread.title,
          description: thread.description,
          category: thread.category,
          statistics: thread.statistics
        } : {
          id: threadId,
          title: 'Discussion',
          description: '',
          category: 'general',
          statistics: {
            messageCount: filteredMessages.length
          }
        }
      };

    } catch (error) {
      console.error('[forumService] Error getting thread messages:', error);
      throw error;
    }
  }

  /**
   * Handle message post event
   */
  async handleMessagePost(payload) {
    const { tripId, threadId, messageId, authorId } = payload;
    // Additional processing for message posts
    console.log(`Message ${messageId} posted in thread ${threadId}`);
  }

  /**
   * Handle thread creation event
   */
  async handleThreadCreation(payload) {
    const { tripId, threadId, userId, title } = payload;
    // Additional processing for thread creation
    console.log(`Thread "${title}" created in trip ${tripId}`);
  }

  /**
   * Handle user joining trip event
   */
  async handleUserJoinTrip(payload) {
    const { tripId, userId, role } = payload;

    // Automatically add user to forum if they join the trip
    const forum = this.activeForums.get(tripId);
    if (forum && !forum.participants.has(userId)) {
      await this.addUserToForum(tripId, userId, role, forum.ownerId);
    }
  }

  /**
   * Handle permission change event
   */
  async handlePermissionChange(payload) {
    const { tripId, userId, oldRole, newRole } = payload;

    const forum = this.activeForums.get(tripId);
    if (forum && forum.participants.has(userId)) {
      const participant = forum.participants.get(userId);
      participant.role = newRole;
      participant.permissions = this.getRolePermissions(newRole);

      eventBridgeService.publish('FORUM_PERMISSION_UPDATED', {
        tripId,
        userId,
        oldRole,
        newRole,
        timestamp: new Date().toISOString()
      });
    }
  }

  /**
   * Check if user has specific permission
   */
  hasPermission(userId, tripId, permission) {
    const forum = this.activeForums.get(tripId);
    if (!forum) return false;

    const participant = forum.participants.get(userId);
    if (!participant) return false;

    return participant.permissions.includes(permission);
  }

  /**
   * Get permissions for a role
   */
  getRolePermissions(role) {
    const rolePermissions = {
      admin: ['create_threads', 'post_messages', 'invite_users', 'manage_permissions', 'manage_threads', 'moderate'],
      collaborator: ['create_threads', 'post_messages', 'invite_users'],
      viewer: ['post_messages']
    };

    return rolePermissions[role] || [];
  }

  /**
   * Extract mentions from message content
   */
  extractMentions(content) {
    const mentionRegex = /@(\w+)/g;
    const mentions = [];
    let match;

    while ((match = mentionRegex.exec(content)) !== null) {
      mentions.push(match[1]);
    }

    return mentions;
  }

  /**
   * Process user mentions in messages
   */
  async processMentions(message) {
    for (const mentionedUser of message.mentions) {
      eventBridgeService.publish('USER_MENTIONED', {
        tripId: message.tripId,
        threadId: message.threadId,
        messageId: message.id,
        mentionedUser,
        mentionedBy: message.authorId,
        timestamp: new Date().toISOString()
      });
    }
  }

  /**
   * Notify forum participants of events
   */
  async notifyParticipants(tripId, eventType, eventData) {
    const forum = this.activeForums.get(tripId);
    if (!forum) return;

    for (const [userId, participant] of forum.participants) {
      if (this.shouldNotifyUser(participant, eventType)) {
        eventBridgeService.publish('FORUM_NOTIFICATION', {
          tripId,
          userId,
          eventType,
          eventData,
          timestamp: new Date().toISOString()
        });
      }
    }
  }

  /**
   * Notify thread participants of new messages
   */
  async notifyThreadParticipants(thread, eventType, eventData) {
    for (const participantId of thread.participants) {
      const forum = this.activeForums.get(thread.tripId);
      const participant = forum?.participants.get(participantId);

      if (participant && this.shouldNotifyUser(participant, eventType)) {
        eventBridgeService.publish('FORUM_NOTIFICATION', {
          tripId: thread.tripId,
          threadId: thread.id,
          userId: participantId,
          eventType,
          eventData,
          timestamp: new Date().toISOString()
        });
      }
    }
  }

  /**
   * Check if user should be notified for event type
   */
  shouldNotifyUser(participant, eventType) {
    const settings = participant.notificationSettings;

    switch (eventType) {
      case 'new_thread':
        return settings.newThreads;
      case 'new_message':
        return settings.newMessages;
      case 'mention':
        return settings.mentions;
      default:
        return true;
    }
  }

  /**
   * Get forum statistics
   *
   * ARCHITECTURE CHANGE: Now retrieves from Firestore
   * - Delegates to discussionFirestoreService.getForumStats()
   * - Returns statistics about messages and participants
   *
   * @param {string} tripId - Trip identifier
   * @param {string} effectiveUserId - Trip owner ID for shared trips (REQUIRED)
   * @returns {Promise<Object>} Forum statistics
   */
  async getForumStatistics(tripId, effectiveUserId) {
    try {
      if (!effectiveUserId) {
        throw new Error('effectiveUserId is required for forum statistics');
      }

      // Get statistics from Firestore
      const firestoreStats = await discussionFirestoreService.getForumStats(effectiveUserId, tripId);

      // Merge with cached forum data if available
      const forum = this.activeForums.get(tripId);

      return {
        ...firestoreStats,
        // Include thread categories if forum is cached
        categories: forum ? this.getThreadCategories(forum) : {},
        // Firestore already provides activeParticipants
        activeParticipants: firestoreStats.activeParticipants
      };

    } catch (error) {
      console.error('[forumService] Error getting forum statistics:', error);
      throw error;
    }
  }

  /**
   * Get thread categories with counts
   *
   * PHASE 1: Simplified - single thread category
   */
  getThreadCategories(forum) {
    // Phase 1: Single 'general' category
    return { general: 1 };
  }

  /**
   * Get top contributors by message count
   *
   * PHASE 1: Now requires Firestore query
   * - This method is deprecated for Phase 1
   * - Use discussionFirestoreService.getForumStats() instead
   *
   * @deprecated Use getForumStats() which queries Firestore
   */
  getTopContributors(forum) {
    console.warn('[forumService] getTopContributors is deprecated. Use getForumStats() instead.');
    // Return empty array for Phase 1
    return [];
  }

  /**
   * Get recent activity summary
   *
   * PHASE 1: Now requires Firestore query
   * - Use discussionFirestoreService.getForumStats() which includes recentActivity
   *
   * @deprecated Use getForumStats() which queries Firestore
   */
  getRecentActivity(forum) {
    console.warn('[forumService] getRecentActivity is deprecated. Use getForumStats() instead.');
    // Return empty array for Phase 1
    return [];
  }
}

// Create singleton instance
const forumService = new ForumService();

export { forumService };
export default forumService;